import React, { useEffect, useMemo, useState } from 'react';

import {
    GetPaperPlanMappingsInput,
    GetPaperPlanMappingsOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import closeCircleTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import plusCircleTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { ColumnState, CsvExportParams, GridApi } from 'ag-grid-community';
import { Switch, useHistory, useLocation, useRouteMatch } from 'react-router';
import { contractConfigurationClient } from 'src/api/client';
import { AddPaperPlanMapping } from 'src/components/AddPaperPlanMapping';
import { FilterInput } from 'src/components/FilterInput';
import { getFullPaperPlanMappings } from 'src/components/PaperPlanMappings/utils/paginator';
import { ProtectedRoute } from 'src/components/ProtectedRoute';
import { Status } from 'src/constants/enums';
import { MAPLE_ROLE_SET } from 'src/constants/role';
import { ROUTES, URL_PARAMS } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IPaperPlanMappingRowData } from 'src/types/IPPRPlanMappings';
import { getCurrentDate } from 'src/util/dateTimeUtil';

import { BaseAgGrid } from '../BaseAgGrid';
import { CancelPaperPlanMappingModal } from './CancelPaperPlanMappingModal';
import './PaperPlanMappings.scss';
import { paperPlanMappingsColDefs } from './paperPlanMappingsColDefs';

export const PaperPlanMappings = () => {
    const [paperPlanMappingsRowData, setPaperPlanMappingsRowData] = useState<IPaperPlanMappingRowData[]>([]);
    const [selectedPlanMappingToCancel, setSelectedPlanMappingToCancel] = useState<IPaperPlanMappingRowData>();
    const [filterText, setFilterText] = useState('');
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const [totalItems, setTotalItems] = useState<number>(0);
    const { path } = useRouteMatch();
    const { pathname } = useLocation();
    const history = useHistory();
    const { setTopAlertMsg, userRole } = React.useContext(AppContext);
    const sortModel: ColumnState[] = [{ colId: 'lastUpdated', sort: 'desc', sortIndex: 0 }];

    const colDefs = useMemo(() => {
        const actionCol = {
            filter: false,
            cellRenderer: (params: any) => (
                <Column height="100%" alignmentVertical="center">
                    <Row spacing="200">
                        {!params.data.isDeleted && (
                            <Button
                                type="link"
                                size="small"
                                href={`${ROUTES.PAPER_PLAN_MAPPINGS.INDEX}${ROUTES.PAPER_PLAN_MAPPINGS.EDIT}/${params.data.paperPlanMappingId}${ROUTES.PAPER_PLAN_MAPPINGS.STEP.DETAILS}`}
                                onClick={history.push}
                                disabled={!MAPLE_ROLE_SET.has(userRole)}
                            >
                                <Icon className="button-icon" tokens={editTokens} />
                            </Button>
                        )}
                    </Row>
                </Column>
            ),
            minWidth: 120,
            maxWidth: 120
        };

        return [...paperPlanMappingsColDefs, actionCol];
    }, []);

    useEffect(() => {
        if (pathname !== ROUTES.PAPER_PLAN_MAPPINGS.INDEX) return;

        getPlanMappings();
    }, [pathname]);

    const getPlanMappings = () => {
        const input = GetPaperPlanMappingsInput.clone({
            includeDeleted: true,
            includeDraftPlans: true
        });

        getFullPaperPlanMappings(input)
            .then((response: GetPaperPlanMappingsOutput) => {
                setIsDataLoaded(true);
                setPaperPlanMappingsRowData(mapResponseToRowData(response) || []);
            })
            .catch((error: any) => {
                if (error instanceof UnknownCoralError) {
                    setTopAlertMsg(error.message);
                } else {
                    setTopAlertMsg(`Unknown error: ${error.message || error}`);
                }
            });
    };

    const mapResponseToRowData = (response: GetPaperPlanMappingsOutput) => {
        return response.paperPlanMappings?.map((paperPlanMapping) => {
            const basePlanPromoIdValue = paperPlanMapping.basePromotions?.length
                ? paperPlanMapping.basePromotions?.[0].promotionId
                : '-';
            const basePlanMappingNameValue =
                (paperPlanMapping.basePlan?.planName || '') +
                (paperPlanMapping.basePromotions?.length ? paperPlanMapping.basePromotions?.[0].promotionId : '');
            const addOnMappingNameValue = paperPlanMapping.addOnPromotions?.length
                ? paperPlanMapping.addOnPromotions
                      ?.map(
                          (addOnPromotion) =>
                              (addOnPromotion.subscriptionPlanId || '') + (addOnPromotion.promotionId || '')
                      )
                      .join(', ')
                : '-';
            return {
                paperPlanMappingId: paperPlanMapping.paperPlanMappingId || '',
                paperPlanId: paperPlanMapping.paperPlan?.paperPlanId || '',
                paperPlanName: paperPlanMapping.paperPlan?.paperPlanName || '',
                basePlanName: paperPlanMapping.basePlan?.planName || '',
                basePlanPromoId: basePlanPromoIdValue,
                territoryCode: paperPlanMapping.territoryCode || '',
                startDate: paperPlanMapping.startDate?.toString() || '',
                endDate: paperPlanMapping.endDate?.toString() || '',
                lastUpdated: paperPlanMapping.changeDate?.toString() || paperPlanMapping.creationDate?.toString() || '',
                updatedBy: paperPlanMapping.changedBy || paperPlanMapping.createdBy || '',
                createdBy: paperPlanMapping.createdBy || '',
                creationDate: paperPlanMapping.creationDate?.toString() || '',
                description: paperPlanMapping.description || '',
                basePlanMappingName: basePlanMappingNameValue,
                addOnMappingName: addOnMappingNameValue,
                isDeleted: paperPlanMapping.isDeleted
            } as IPaperPlanMappingRowData;
        });
    };

    const setDefaultFilter = () => {
        const statusFilterModel = {
            status: {
                filterType: 'set',
                values: [Status.Active, Status.Ended, Status.Upcoming, Status.Draft]
            }
        };
        agGridApi?.setFilterModel(statusFilterModel);
    };

    useEffect(() => {
        if (agGridApi) setDefaultFilter();
    }, [agGridApi]);

    const handlePlanMappingModalOnClose = (refreshData = false) => {
        setSelectedPlanMappingToCancel(undefined);
        if (refreshData) {
            getPlanMappings();
        }
    };

    const exportPlanMappings = () => {
        const params: CsvExportParams = {
            fileName: `plan_mapping_export_${getCurrentDate()}.csv`,
            columnKeys: paperPlanMappingsColDefs.flatMap((colDef) => (colDef.field ? [colDef.field] : []))
        };
        agGridApi?.exportDataAsCsv(params);
    };

    const paperPlanMappingsContent = (
        <Column className="paper-plan-mappings-content" spacing="300" width="100%" height="100%" minHeight={480}>
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <Column>
                        <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    </Column>
                    <div>
                        <Button type="link" size="small" onClick={exportPlanMappings}>
                            <Icon tokens={downloadLargeTokens} />
                            Download
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            href={`${ROUTES.PAPER_PLAN_MAPPINGS.INDEX}${ROUTES.PAPER_PLAN_MAPPINGS.ADD}${ROUTES.PAPER_PLAN_MAPPINGS.STEP.DETAILS}`}
                            onClick={history.push}
                            disabled={!MAPLE_ROLE_SET.has(userRole)}
                        >
                            <Icon tokens={plusCircleTokens} />
                            Add mapping
                        </Button>
                    </div>
                </Row>
            </Box>
            <div className="paper-plan-mappings-grid">
                <BaseAgGrid
                    columnDefs={colDefs}
                    rowData={paperPlanMappingsRowData}
                    filterText={filterText}
                    sortModel={sortModel}
                    setAgGridApi={setAgGridApi}
                    onFirstDataRendered={setDefaultFilter}
                    loading={!isDataLoaded}
                />
            </div>
        </Column>
    );

    return (
        <div className="paper-plan-mappings-container">
            <Switch>
                <ProtectedRoute exact path={path}>
                    {paperPlanMappingsContent}
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={MAPLE_ROLE_SET} path={`${path}${ROUTES.PAPER_PLAN_MAPPINGS.ADD}`}>
                    <AddPaperPlanMapping />
                </ProtectedRoute>
                <ProtectedRoute
                    requiredRoleSet={MAPLE_ROLE_SET}
                    path={`${path}${ROUTES.PAPER_PLAN_MAPPINGS.EDIT}${URL_PARAMS.PAPER_PLAN_MAPPING_ID}`}
                >
                    <AddPaperPlanMapping />
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
